import { h, Component, Fragment } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import { route } from 'preact-router';

import lockScreenStore from '../../stores/lockScreen';
import activityTracker from '../../lib/activityTracker';
import localUtil from '../../lib/util';
import { observeReportButtonPosition } from '../../lib/reportButtonPosition';
import { createHelpSearchLogger } from '../../lib/helpSearchLogger';
import { getHelpContextTags, getTopDrawerComponent, getHelpContextKey } from '../../lib/helpPageContext';
import { splitBodySegments, addBylinesToImages, fixImageSrc } from '../articleRender/articleContentProcessor';
import MarkdownDisplay from '../gui/markdownDisplay';
import FactBox from '../articleRender/factBox';
import ReportProblem from '../support/reportProblem';
import './widget.css';

/*
 * In-page help panel behind the floating "Hjelp" pill (replaces the floating "Meld et problem"
 * button, which now sits at the bottom of this panel as the last resort).
 *
 * The panel is hidden — never unmounted — when closed, so the query, the result list, the AI
 * answer and the open article (including its scroll position) are all there when it is reopened.
 * The footer keeps this component mounted across route changes, so that also survives navigation.
 */

const SEARCH_DEBOUNCE_MS = 700;
const SEARCH_MIN_CHARS = 2;
const SEARCH_LIMIT = 8;
const POPULAR_LIMIT = 5;
// "Help for this page": articles tagged for the current route / drawer, shown above the popular list.
const CONTEXT_LIMIT = 5;
// Same threshold as the help center: no AI on 1-2 character queries.
const AI_MIN_CHARS = 3;
// Mirrors the help center: the search also covers news and the frontpage "main" articles.
const SEARCH_CATEGORIES = ['help', 'news'];
// Not shown while the member is already in the help center.
const HELP_ROUTE = /^\/help(?:[/?]|$)/;
// Segment types the panel renders inline; the rest (video, poll, results, product, read-also)
// only render in the full help center, and the panel says so with a link.
const INLINE_SEGMENTS = ['markdown', 'factbox'];

// Non-help hits (news, frontpage) open on their own page — same as the help center.
function articlePageRoute({ uuidv4, title }) {
    const slug = encodeURIComponent(title || '').toLocaleLowerCase();
    return `/article/${slug}/${uuidv4}`;
}

function categoryBadge(article) {
    if (!article || !article.category || article.category === 'help') {
        return null;
    }
    return (
        <span class='badge text-bg-secondary ms-2' style='font-size: 0.7em; vertical-align: middle;'>
            {article.category === 'news'
                ? <Text id='help.article-category-news'>News</Text>
                : <Text id='help.article-category-main'>Frontpage</Text>}
        </span>
    );
}

function excerpt(article) {
    const text = String(article.ingress || article.body || '')
        .replace(/\{\{[^}]*\}\}/g, ' ')
        .replace(/[#*_`>[\]()!-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    return text.length > 90 ? `${text.substring(0, 90)}...` : text;
}

function hasVideoFile(article) {
    return (article.files || []).some(f => f.s3LinkStream || /\.(m4v|m4a|mp4|mov)$/i.test(f.s3Link || ''));
}

const stopKey = event => event.stopPropagation();

@observer
class HelpWidget extends Component {
    constructor(props) {
        super(props);
        this.state = {
            open: false,
            // The panel is mounted on first open and kept mounted (hidden) afterwards.
            everOpened: false,
            // Hidden while the report button captures the screen so the screenshot shows the page.
            capturing: false,
            search: '',
            results: [],
            searched: false,
            loading: false,
            popular: [],
            popularLoaded: false,
            // "Help for this page" articles for the page the panel was last opened on.
            contextArticles: [],
            article: null,
            articleLoading: false,
            articleFailed: false,
            aiLoading: false,
            aiSuggestion: null,
            // Article image shown large above the panel: { src, alt } or null.
            lightbox: null,
        };
        // Monotonic tokens so stale search / AI responses are ignored when the query moves on.
        this.searchSeq = 0;
        this.aiSeq = 0;
        // Which page (route + drawer) contextArticles were loaded for; kept off state so the
        // guard below sees it synchronously, before Preact flushes.
        this.contextKey = null;
        this.searchTimer = null;
        this.helpSearchLogger = createHelpSearchLogger({
            log: ({ search, hits }) => {
                const { helpSearchStore } = this.props.stores;
                if (helpSearchStore) {
                    helpSearchStore.logSearch({ search, hits });
                }
            },
        });
    }

    get canShow() {
        const { userStore, appState } = this.props.stores;
        return !!userStore.user?.id && !lockScreenStore.isLocked && !HELP_ROUTE.test(appState.path || '');
    }

    componentDidMount() {
        this.updatePositionObserver();
    }

    componentDidUpdate() {
        this.updatePositionObserver();
    }

    componentWillUnmount() {
        this.stopObservingPosition?.();
        document.removeEventListener('keydown', this.lightboxKeyDown, true);
        clearTimeout(this.searchTimer);
        this.helpSearchLogger.cancel();
        this.searchSeq += 1;
        this.aiSeq += 1;
    }

    // Same drawer-aware fixed positioning the report pill used, applied to the whole widget.
    updatePositionObserver() {
        if (this.observedRoot === this.root) return;
        this.stopObservingPosition?.();
        this.observedRoot = this.root;
        this.stopObservingPosition = this.root ? observeReportButtonPosition(this.root) : null;
    }

    open = () => {
        this.setState({ open: true, everOpened: true }, () => {
            if (!this.state.popularLoaded) {
                this.loadPopular();
            }
            this.loadContext();
            if (!this.state.article) {
                this.input?.focus();
            }
        });
    }

    close = () => {
        // Don't leave keyboard focus on an element inside the now-hidden panel.
        const focusInside = this.panel?.contains(document.activeElement);
        this.closeLightbox();
        this.setState({ open: false }, () => {
            if (focusInside) this.pill?.focus();
        });
    }

    toggle = () => {
        if (this.state.open) {
            this.close();
        } else {
            this.open();
        }
    }

    loadPopular = async () => {
        const { articleStore } = this.props.stores;
        this.setState({ popularLoaded: true });
        let articles = [];
        try {
            articles = await articleStore.load({
                query: {
                    hostname: articleStore.currentHostname,
                    category: 'help',
                    all: 1,
                },
                sort: '-totalViews',
                limit: POPULAR_LIMIT,
                skipUpdate: true,
            });
        } catch (error) {
            articles = [];
        }
        this.setState({ popular: Array.isArray(articles) ? articles : [] });
    }

    // Where the member is right now: the route plus the topmost open drawer.
    currentContext() {
        const { appState } = this.props.stores;
        return { path: appState.path || '', drawer: getTopDrawerComponent(appState) };
    }

    // Loads "help for this page" once per page; reopening on the same page reuses the list.
    loadContext = async () => {
        const { articleStore } = this.props.stores;
        const context = this.currentContext();
        const contextKey = getHelpContextKey(context);
        if (contextKey === this.contextKey) return;
        this.contextKey = contextKey;
        const tags = getHelpContextTags(context);
        this.setState({ contextArticles: [] });
        if (tags.length === 0) return;
        let articles = [];
        try {
            articles = await articleStore.load({
                query: {
                    hostname: articleStore.currentHostname,
                    category: 'help',
                    aiTopicTags: tags,
                    all: 1,
                },
                sort: '-totalViews',
                limit: CONTEXT_LIMIT,
                skipUpdate: true,
            });
        } catch (error) {
            articles = [];
        }
        if (contextKey !== this.contextKey) return; // the member moved on meanwhile
        this.setState({ contextArticles: Array.isArray(articles) ? articles : [] });
    }

    searchInput = (e) => {
        const search = e.target.value;
        // A new query drops the old AI answer and any open article; the AI only runs on request.
        this.aiSeq += 1;
        clearTimeout(this.searchTimer);
        if (search.trim().length < SEARCH_MIN_CHARS) {
            this.searchSeq += 1;
            this.helpSearchLogger.cancel();
            this.setState({
                search, results: [], searched: false, loading: false, aiLoading: false, aiSuggestion: null, article: null,
            });
            return;
        }
        this.setState({
            search, loading: true, aiLoading: false, aiSuggestion: null, article: null,
        });
        this.searchTimer = setTimeout(this.doSearch, SEARCH_DEBOUNCE_MS);
    }

    doSearch = async () => {
        const search = this.state.search.trim();
        const { articleStore } = this.props.stores;
        this.searchSeq += 1;
        const seq = this.searchSeq;
        let articles = [];
        try {
            articles = await articleStore.load({
                query: {
                    hostname: articleStore.currentHostname,
                    category: SEARCH_CATEGORIES,
                    includeMainArticles: 1,
                    stemmedSearch: 1,
                    search,
                    all: 1,
                },
                limit: SEARCH_LIMIT,
                skipUpdate: true,
            });
        } catch (error) {
            articles = [];
        }
        if (seq !== this.searchSeq) return;
        const results = Array.isArray(articles) ? articles : [];
        this.setState({ results, searched: true, loading: false });
        this.helpSearchLogger.onSearchResult(search, results.length);
    }

    clearSearch = () => {
        this.aiSeq += 1;
        this.searchSeq += 1;
        clearTimeout(this.searchTimer);
        this.helpSearchLogger.cancel();
        this.setState({
            search: '', results: [], searched: false, loading: false, aiLoading: false, aiSuggestion: null, article: null,
        }, () => this.input?.focus());
    }

    openArticle = async (row) => {
        if (row.category && row.category !== 'help' && row.uuidv4) {
            this.close();
            route(articlePageRoute(row));
            return;
        }
        const { articleStore, appState } = this.props.stores;
        this.setState({ article: { id: row.id, title: row.title }, articleLoading: true, articleFailed: false });
        let data = null;
        try {
            // skipUpdate: never clobber articleStore.article, which an open article page may be showing.
            data = await articleStore.load(row.id, false, {
                query: {
                    hostname: articleStore.currentHostname,
                    category: 'help',
                    all: 1,
                },
                skipUpdate: true,
            });
        } catch (error) {
            data = null;
        }
        const article = Array.isArray(data) ? data[0] : data;
        if (this.state.article?.id !== row.id) return; // the member moved on meanwhile
        if (!article?.id) {
            this.setState({ articleLoading: false, articleFailed: true });
            return;
        }
        appState.trackArticle('view', article);
        this.setState({ article, articleLoading: false, articleFailed: false }, () => {
            if (this.body) this.body.scrollTop = 0;
        });
    }

    backToResults = () => {
        this.setState({ article: null, articleLoading: false, articleFailed: false }, () => this.input?.focus());
    }

    // Runs only when the member clicks "Spør AI" — never automatically while typing.
    runAi = async () => {
        const q = this.state.search.trim();
        const { articleStore } = this.props.stores;
        if (q.length < AI_MIN_CHARS) return;
        this.aiSeq += 1;
        const seq = this.aiSeq;
        this.setState({ aiLoading: true });
        try {
            const data = await articleStore.getSearchSuggestion(q);
            if (seq !== this.aiSeq) return;
            this.setState({ aiLoading: false, aiSuggestion: data || { suggestedAnswer: null } });
        } catch (error) {
            if (seq !== this.aiSeq) return;
            this.setState({ aiLoading: false, aiSuggestion: { suggestedAnswer: null } });
        }
    }

    openHelpCenter = () => {
        const { article } = this.state;
        const search = this.state.search.trim();
        this.close();
        if (article?.id) {
            route(`/help/${article.id}`);
        } else if (search) {
            route(`/help/search/${encodeURIComponent(search)}`);
        } else {
            route('/help');
        }
    }

    captureStart = () => {
        this.setState({ open: false, capturing: true });
    }

    captureEnd = () => {
        this.setState({ capturing: false });
    }

    keyDown = (event) => {
        activityTracker.onActivity();
        event.stopPropagation();
        if (event.key === 'Escape') {
            event.preventDefault();
            this.close();
        }
    }

    // The article body is innerHTML from the markdown pipeline, so image clicks are caught here by delegation.
    articleBodyClick = (event) => {
        const target = event.target;
        if (!target || target.tagName !== 'IMG') return;
        const src = target.currentSrc || target.src;
        if (!src) return;
        event.preventDefault();
        // Capture phase: the panel's own keydown handler stops propagation and would close the whole panel,
        // and after a click on an image the focus is usually on <body>, outside the panel anyway.
        document.addEventListener('keydown', this.lightboxKeyDown, true);
        this.setState({ lightbox: { src, alt: target.alt || '' } });
    }

    closeLightbox = () => {
        document.removeEventListener('keydown', this.lightboxKeyDown, true);
        if (this.state.lightbox) this.setState({ lightbox: null });
    }

    lightboxKeyDown = (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        this.closeLightbox();
    }

    renderRow = (row, keyPrefix) => (
        <button
            key={`${keyPrefix}-${row.id}`}
            type='button'
            class='list-group-item list-group-item-action px-2 py-2 help-widget-row'
            onClick={() => this.openArticle(row)}
        >
            <div class='d-flex align-items-center'>
                <span class='fw-semibold'>{row.title}</span>
                {categoryBadge(row)}
            </div>
            {excerpt(row) && <small class='text-body-secondary d-block'>{excerpt(row)}</small>}
        </button>
    )

    renderAi = () => {
        const { aiLoading, aiSuggestion } = this.state;
        // Strip the [Title](/article/uuid) link syntax — the referenced articles are listed as rows below.
        const answer = aiSuggestion?.suggestedAnswer
            ? aiSuggestion.suggestedAnswer.replace(/\[([^\]]+)\]\(\/article\/[a-f0-9-]+\)/g, '$1')
            : '';
        const refs = (aiSuggestion?.referencedArticles || []).filter(r => r.id > 0);
        return (
            <div class='help-widget-ai border-top pt-3 mt-3'>
                <div class='d-flex align-items-center mb-2'>
                    <span
                        class='d-inline-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary me-2'
                        style='width: 1.75rem; height: 1.75rem;'
                    >
                        <i class='fa-solid fa-robot' />
                    </span>
                    <strong><Text id='help.widget.aiTitle'>AI answer</Text></strong>
                </div>
                {aiLoading ? (
                    <div class='text-body-secondary small'>
                        <i class='fa-solid fa-spinner fa-spin me-1' />
                        <Text id='help.widget.aiThinking'>Thinking...</Text>
                    </div>
                ) : answer ? (<>
                    <div class='small'>
                        <MarkdownDisplay markdown={answer} markedOpts={localUtil.getMarkdownOptions()} stores={this.props.stores} />
                    </div>
                    {refs.length > 0 && (
                        <div class='mt-2'>
                            <small class='fw-bold text-body-secondary text-uppercase' style='font-size: 0.7em; letter-spacing: 0.05em;'>
                                <Text id='help.widget.aiRelated'>Related articles</Text>
                            </small>
                            <div class='list-group list-group-flush mt-1'>
                                {refs.map(r => this.renderRow(r, 'ai'))}
                            </div>
                        </div>
                    )}
                    <small class='text-body-tertiary fst-italic d-block mt-2'>
                        <i class='fa-solid fa-circle-info me-1' />
                        <Text id='help.widget.aiDisclaimer'>AI generated answer. Check the articles above.</Text>
                    </small>
                </>) : aiSuggestion ? (
                    <div class='text-body-secondary small'>
                        <Text id='help.widget.aiNoAnswer'>No clear answer was found. See the articles above.</Text>
                    </div>
                ) : (
                    <div>
                        <div class='text-body-secondary small mb-2'>
                            <Text id='help.widget.aiIntro'>Want an AI answer to your search? It is based on the help articles.</Text>
                        </div>
                        <button type='button' class='btn btn-sm btn-primary rounded-pill help-widget-ask-ai' onClick={this.runAi}>
                            <i class='fa-solid fa-wand-magic-sparkles me-1' />
                            <Text id='help.widget.askAi'>Ask AI</Text>
                        </button>
                    </div>
                )}
            </div>
        );
    }

    renderList = () => {
        const { search, results, searched, loading, popular, contextArticles } = this.state;
        const query = search.trim();
        const isSearch = query.length >= SEARCH_MIN_CHARS;
        const showContext = !isSearch && contextArticles.length > 0;
        // An article already listed for this page is not repeated in the most-read list.
        const contextIds = new Set(contextArticles.map(a => a.id));
        const rows = isSearch ? results : popular.filter(a => !showContext || !contextIds.has(a.id));
        return (<>
            {showContext && (
                <div class='help-widget-context mb-3'>
                    <h6 class='fw-bold text-body-secondary text-uppercase mb-2' style='font-size: 0.75em; letter-spacing: 0.05em;'>
                        <i class='fa-solid fa-location-dot me-1' />
                        <Text id='help.widget.forThisPage'>Help for this page</Text>
                    </h6>
                    <div class='list-group list-group-flush'>
                        {contextArticles.map(row => this.renderRow(row, 'context'))}
                    </div>
                </div>
            )}
            <h6 class='fw-bold text-body-secondary text-uppercase mb-2' style='font-size: 0.75em; letter-spacing: 0.05em;'>
                {isSearch
                    ? <Text id='help.widget.results'>Results</Text>
                    : <Text id='help.widget.popular'>Most read articles</Text>}
            </h6>
            {loading && (
                <div class='text-body-secondary small py-2'>
                    <i class='fa-solid fa-spinner fa-spin me-1' />
                    <Text id='help.widget.searching'>Searching...</Text>
                </div>
            )}
            {!loading && rows.length > 0 && (
                <div class='list-group list-group-flush'>
                    {rows.map(row => this.renderRow(row, isSearch ? 'hit' : 'popular'))}
                </div>
            )}
            {!loading && isSearch && searched && rows.length === 0 && (
                <div class='text-body-secondary small py-2'>
                    <i class='fa-solid fa-circle-info me-1' />
                    <Text id='help.widget.noResults'>No articles found</Text>
                </div>
            )}
            {!loading && searched && query.length >= AI_MIN_CHARS && this.renderAi()}
        </>);
    }

    renderArticleBody = (article) => {
        const { userStore } = this.props.stores;
        const { language } = userStore;
        const segments = splitBodySegments(article.body || '');
        const hasBylines = (article.images || []).some(img => img?.caption || img?.photographer);
        const richContent = segments.some(s => !INLINE_SEGMENTS.includes(s.type)) || hasVideoFile(article);
        const process = (content) => {
            let text = fixImageSrc(localUtil.replaceImages(content, article.images, language, false));
            if (hasBylines) {
                text = addBylinesToImages(text, article.images);
            }
            return text;
        };
        return (<>
            {richContent && (
                <div class='alert alert-light border small py-2 px-3 mb-3'>
                    <i class='fa-solid fa-circle-info me-1' />
                    <Text id='help.widget.richContent'>Videos and polls in this article are only shown in the Help Center.</Text>
                </div>
            )}
            {segments.map((segment, idx) => {
                if (segment.type === 'markdown') {
                    return (
                        <MarkdownDisplay
                            key={`seg-${idx}`}
                            markdown={process(segment.content)}
                            markedOpts={localUtil.getMarkdownOptions()}
                            stores={this.props.stores}
                        />
                    );
                }
                if (segment.type === 'factbox') {
                    return <FactBox key={`fb-${segment.articleId}`} articleId={segment.articleId} stores={this.props.stores} />;
                }
                return null;
            })}
        </>);
    }

    renderArticle = () => {
        const { article, articleLoading, articleFailed } = this.state;
        return (
            <div class='help-widget-article'>
                <h5 class='fw-bold mb-2'>{article.title}</h5>
                <button
                    type='button'
                    class='btn btn-link btn-sm text-decoration-none p-0 mb-3 help-widget-open-help-center'
                    onClick={this.openHelpCenter}
                >
                    <i class='fa-solid fa-arrow-up-right-from-square me-1' />
                    <Text id='help.widget.openArticleInHelpCenter'>Open in the Help Center</Text>
                </button>
                {articleLoading && (
                    <div class='text-body-secondary small py-2'>
                        <i class='fa-solid fa-spinner fa-spin me-1' />
                        <Text id='help.widget.searching'>Searching...</Text>
                    </div>
                )}
                {articleFailed && (
                    <div class='alert alert-warning small py-2 px-3'>
                        <Text id='help.widget.articleFailed'>Could not load the article.</Text>
                    </div>
                )}
                {!articleLoading && !articleFailed && (
                    <div class='help-widget-article-body small' onClick={this.articleBodyClick}>
                        {this.renderArticleBody(article)}
                    </div>
                )}
            </div>
        );
    }

    renderLightbox = () => {
        const { lightbox } = this.state;
        if (!lightbox) return null;
        return (
            <div class='help-widget-lightbox' role='dialog' aria-modal='true' onClick={this.closeLightbox}>
                <button type='button' class='btn btn-lg btn-link text-white help-widget-lightbox-close' onClick={this.closeLightbox}>
                    <i class='fa-solid fa-xmark' />
                    <span class='visually-hidden'><Text id='help.widget.closeImage'>Close image</Text></span>
                </button>
                <img src={lightbox.src} alt={lightbox.alt} onClick={event => event.stopPropagation()} />
            </div>
        );
    }

    render() {
        if (!this.canShow) return null;
        const { open, everOpened, capturing, search, article } = this.state;
        return (
            <div
                class='help-widget'
                ref={element => { this.root = element; }}
                style={{ visibility: capturing ? 'hidden' : 'visible' }}
            >
                {everOpened && (
                    <div
                        class={`help-widget-panel card shadow-lg bg-body text-body border${open ? '' : ' help-widget-panel-hidden'}`}
                        role='dialog'
                        aria-modal='false'
                        aria-labelledby='help-widget-title'
                        aria-hidden={!open}
                        ref={element => { this.panel = element; }}
                        onKeyDown={this.keyDown}
                        onKeyUp={stopKey}
                        onKeyPress={stopKey}
                    >
                        <div class='card-header d-flex align-items-center gap-2 py-2 px-2'>
                            {article ? (
                                <button type='button' class='btn btn-sm btn-link text-body p-1 help-widget-back' onClick={this.backToResults}>
                                    <i class='fa-solid fa-arrow-left' />
                                    <span class='visually-hidden'><Text id='help.widget.back'>Back</Text></span>
                                </button>
                            ) : <span style='width: 1.75rem;' />}
                            <strong class='flex-grow-1 text-center' id='help-widget-title'>
                                {article
                                    ? <Text id='help.widget.article'>Help article</Text>
                                    : <Text id='help.widget.title'>Help</Text>}
                            </strong>
                            <button type='button' class='btn btn-sm btn-link text-body p-1 help-widget-close' onClick={this.close}>
                                <i class='fa-solid fa-chevron-down' />
                                <span class='visually-hidden'><Text id='help.widget.close'>Close</Text></span>
                            </button>
                        </div>
                        {!article && (
                            <div class='p-2 border-bottom'>
                                <div class='input-group input-group-sm'>
                                    <span class='input-group-text'><i class='fa-solid fa-magnifying-glass' /></span>
                                    <Localizer>
                                        <input
                                            type='text'
                                            class='form-control'
                                            placeholder={<Text id='help.widget.placeholder'>Search the help articles...</Text>}
                                            value={search}
                                            onInput={this.searchInput}
                                            ref={element => { this.input = element; }}
                                        />
                                    </Localizer>
                                    {search && (
                                        <button type='button' class='btn btn-outline-secondary' onClick={this.clearSearch}>
                                            <i class='fa-solid fa-xmark' />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <div class='help-widget-body p-3' ref={element => { this.body = element; }}>
                            {article ? this.renderArticle() : this.renderList()}
                        </div>
                        <div class='card-footer help-widget-footer d-flex flex-column gap-2 p-2'>
                            {!article && (
                                <button
                                    type='button'
                                    class='btn btn-link btn-sm text-decoration-none p-0 help-widget-open-help-center'
                                    onClick={this.openHelpCenter}
                                >
                                    <i class='fa-solid fa-arrow-up-right-from-square me-1' />
                                    <Text id='help.widget.openHelpCenter'>Open the Help Center</Text>
                                </button>
                            )}
                            <small class='text-body-secondary'><Text id='help.widget.reportHint'>Did not find the answer?</Text></small>
                            <ReportProblem
                                stores={this.props.stores}
                                trigger='panel'
                                onCaptureStart={this.captureStart}
                                onCaptureEnd={this.captureEnd}
                            />
                        </div>
                    </div>
                )}
                <button
                    type='button'
                    class='badge text-bg-primary rounded-pill border-0 px-3 py-2 help-widget-button'
                    onClick={this.toggle}
                    aria-expanded={open}
                    ref={element => { this.pill = element; }}
                >
                    <i class={`fa-solid ${open ? 'fa-chevron-down' : 'fa-circle-question'} me-1`} />
                    <Text id='help.widget.button'>Help</Text>
                </button>
                {this.renderLightbox()}
            </div>
        );
    }
}

export default HelpWidget;
